import React from 'react';
import Highlight from '../Highlight/Highlight.jsx';
import SearchSuggestionsNosto from '../SearchSuggestions/SearchSuggestionsNosto.jsx';
import { recordSearchClick, recordViewProduct } from '@utils/nostoTracking.js';

const SearchFlyoutNosto = ({
    suggestions,
    handleSuggestionClick,
    results,
    query,
    collections = [],
    pages = [],
    loading = false,
    searched = false,
    redirect = null,
}) => {
    // Record search attribution when results are loaded
    React.useEffect(() => {
        if (searched && query && results && results.length > 0) {
            // Record attribution for internal search
            if (typeof window !== 'undefined' && window.nostojs) {
                window.nostojs((api) => {
                    api.recordAttribution({
                        type: 'is', // Internal Search
                        target: query,
                        ref: 'search-flyout'
                    })
                                            .done()
                    .then((data) => {
                        // Attribution recorded successfully
                    })
                    .catch((error) => {
                        console.error('Nosto search attribution error:', error);
                    });
                });
            }
        }
    }, [searched, query, results]);

    // Handle product click attribution
    const handleProductClick = (product) => {
        // Record search click analytics for search flyout
        if (product?.productId && product?.url) {
            recordSearchClick('autocomplete', {
                productId: product.productId,
                url: product.url
            })
        }

        // Record attribution for product click using deduplicated tracking
        if (product?.productId) {
            recordViewProduct(product.productId, 'search-flyout', 'vp')
        }
    };
    // Limit products to first 6 for better flyout performance
    const limitedResults = results?.slice(0, 6) || [];

    const formatRedirectText = (url) => {
        if (!url) return '';

        if (url.includes('/collections/')) {
            const handle = url.split('/collections/')[1].split('/')[0];
            return handle
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        }

        return url.split('/').pop().replace(/_/g, ' - ');
    };

    return (
        <div className='search-flyout-outer w-full h-full'>
            <div className='search-flyout-inner max-w-[1200px] mx-auto'>
                <div className='flyout-sections-outer px-6 pt-2 pb-6 md:p-6 w-full'>
                    <div className='flyout-sections-inner flex flex-col md:flex-row gap-2 md:gap-8 w-full'>
                        <div className='flyout-section flyout-section-outer products-section md:flex-1 mt-1 mb-4 order-2 md:order-1'>
                            <h3 className='flyout-section-title text-base md:text-lg mb-2 flex items-center'>
                                Products
                                <a
                                    href={`/search?q=${query}`}
                                    className='view-all text-sm font-normal text-flint hover:text-ash hover:underline cursor-pointer ml-auto'
                                >
                                    View All »
                                </a>
                            </h3>
                            {loading ? (
                                <div className='loading-state'>
                                    <p className='font-normal text-flint text-sm'>
                                        Searching...
                                    </p>
                                </div>
                            ) : searched && query && limitedResults.length === 0 ? (
                                <div className='no-results'>
                                    <p className='font-normal text-flint text-sm'>
                                        No results found
                                    </p>
                                </div>
                            ) : (
                                <div className="products-grid grid grid-cols-2 gap-4 md:grid-cols-4">
                                    {limitedResults.map((product) => (
                                        <div key={product.productId} className="product-item">
                                            <a
                                                href={`/products/${product.url.split('/').pop().split('_')[0]}?variant=${product.skus?.find(sku => sku.inventoryLevel > 0)?.id || product.skus?.[0]?.id || ''}`}
                                                className="block"
                                                onClick={() => handleProductClick(product)}
                                            >
                                                <div
                                                    className="aspect-[2/3] flex items-center justify-center mb-2"
                                                    style={{ backgroundColor: product.brand === 'Monica + Andy' ? '#f8f8f8' : '#ffffff' }}
                                                >
                                                    <img
                                                        src={product.imageUrl}
                                                        alt={product.name}
                                                        className="w-full h-full object-contain"
                                                        loading="lazy"
                                                    />
                                                </div>
                                                <p className="text-sm text-flint hover:text-ash">
                                                    <Highlight search={query}>
                                                        {product.name.replace(/_/g, ' - ')}
                                                    </Highlight>
                                                </p>
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className='flyout-section flyout-section-outer suggestions-section md:w-64 lg:w-80 mt-0 mb-2 md:mt-1 md:mb-4 order-1 md:order-2'>
                            <SearchSuggestionsNosto
                                suggestions={suggestions}
                                handleSuggestionClick={handleSuggestionClick}
                                query={query}
                            />
                            {redirect && (
                                <div className="redirects-section mt-4">
                                    <h3 className="text-base md:text-lg mb-2">Collections, Pages + Posts</h3>
                                    <div className="redirects-list">
                                        <a
                                            href={redirect}
                                            className="block text-sm text-flint hover:text-ash hover:underline py-1"
                                        >
                                            {formatRedirectText(redirect)}
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchFlyoutNosto;
